import { ErrorContent } from "../components/errorContent";
import {
  useApiErrorTranslation,
  useErrorMessageTranslationBundleQuery,
  useLocalizedApiErrorText,
} from "../hooks/useErrorMessageTranslationBundle";
import { getLocalizedApiErrorMessage } from "../utils/error";
import LoadingScreen from "./loading";
import NotFoundPageSkeleton from "./skeletons/NotFound";

type ErrorPageProp = {
  errorMessage: string;
};

type ContentProp = {
  // Position of [content] in DOM.
  position: string;
  errorMessage: string;
  /** When true, `errorMessage` is already localized (e.g. via {@link useLocalizedApiErrorText}). */
  preLocalized?: boolean;
};

type ApiErrorContentProp = {
  /** Server error text; localized here unless `preLocalized`. */
  errorMessage?: string;
  preLocalized?: boolean;
};

/**
 * This page is rendred when then initial fetching of data, from server, fails.
 *
 * @param {ErrorPageProp} errorPageProp
 *
 * @returns a JSX element representing [ErrorPage].
 */
export function ErrorPage({ errorMessage }: ErrorPageProp) {
  return (
    <NotFoundPageSkeleton>
      <Content position="absolute" errorMessage={errorMessage} />
    </NotFoundPageSkeleton>
  );
}

type ApiExceptionErrorPageProp = {
  errorMessage: string;
};

export const ApiExceptionErrorPage = ({
  errorMessage,
}: ApiExceptionErrorPageProp) => {
  const { isLoading, localized } = useLocalizedApiErrorText(errorMessage);

  return isLoading ? (
    <LoadingScreen />
  ) : (
    <NotFoundPageSkeleton>
      <Content position="absolute" errorMessage={localized} preLocalized />
    </NotFoundPageSkeleton>
  );
};

/**
 * Content of [ErrorPage].
 *
 * @param {ContentProp} contentProp
 *
 * @returns a JSX element representing [ErrorPage].
 */
function Content(contentProp: ContentProp) {
  return (
    <div className={`${contentProp.position} flex h-screen w-screen flex-col`}>
      <div className="mb-48 ml-24 mr-24 mt-48 place-content-evenly">
        <ApiErrorContent
          errorMessage={contentProp.errorMessage}
          preLocalized={contentProp.preLocalized}
        />
      </div>
    </div>
  );
}

/**
 * Localizes a server error message and renders the generic error content,
 * embeddable outside the standalone error page.
 */
export function ApiErrorContent({
  errorMessage,
  preLocalized,
}: ApiErrorContentProp) {
  // The namespace is served by the backend; without this fetch every lookup
  // would miss and the message would render untranslated.
  useErrorMessageTranslationBundleQuery();
  const t = useApiErrorTranslation();

  const localizedErrorMessage: string | undefined =
    errorMessage && !preLocalized
      ? getLocalizedApiErrorMessage(errorMessage, t)
      : errorMessage;
  return <ErrorContent errorMessage={localizedErrorMessage} />;
}
